import { isNil } from "common/utils/identity";
import { number, object } from "yup";
import { TextField, TextFieldProps } from "@mui/material";
import { useShadowValue } from "common/hooks/useShadowValue";
import { useTranslation } from "react-i18next";
import { yupToFormErrors } from "formik";
import React, { FC, useRef, useState } from "react";

// don't let undefined value go in because it messes with the MUI controlled / uncontrolled
const convertToControlSafeValue = (val: number | undefined): string => {
  return val === undefined ? "" : String(val);
};

const convertStringValToNum = (val: string): number | undefined => {
  return val === "" ? undefined : Number(val);
};
interface NumberInputProps {
  label?: React.ReactNode;
  value: number | undefined;
  onChange: (val: number | undefined) => void;
  min?: number;
  max?: number;
  step?: number | "any";
  required?: boolean;
  error?: string;
  textFieldProps?: Partial<TextFieldProps>;
  onValidityChange?: (isValid: boolean) => void;
}
export const NumberInput: FC<NumberInputProps> = ({
  label,
  value,
  onChange,
  min,
  max,
  step,
  required = false,
  error,
  textFieldProps = {},
  onValidityChange,
}) => {
  const inputRef = useRef<HTMLInputElement>();
  const { t } = useTranslation();
  const { slotProps, ...restTextFieldProps } = textFieldProps;

  let valueSchema = number()
    .nullable()
    .typeError(t("utils.form.numberType"))
    .min(
      min ?? -Infinity,
      t("utils.form.mustBeGreaterThanOrEqual", { minimum: min })
    )
    .max(
      max ?? Infinity,
      t("utils.form.mustBeLessThanOrEqual", { maximum: max })
    );
  if (required) {
    valueSchema = valueSchema.required(t("utils.form.required"));
  }
  const validationSchema = object({ value: valueSchema });

  const getErrorMessage = (val: number | undefined): string | undefined => {
    try {
      validationSchema.validateSync({ value: val }, { abortEarly: false });
      return undefined;
    } catch (yupError) {
      const validationErrors = yupToFormErrors<{ value: number }>(yupError);
      return validationErrors.value;
    }
  };

  const [editedValue, setEditedValue, { skipRender: valueHasChanged }] =
    useShadowValue(convertToControlSafeValue(value));
  const [internalError, setInternalError] = useState<string | undefined>();

  // Clear validation error when parent replaces the value (e.g. reset).
  if (valueHasChanged) {
    setInternalError(undefined);
  }

  const validate = (val: number | undefined): boolean => {
    const message = getErrorMessage(val);
    setInternalError(message);
    onValidityChange?.(message === undefined);
    return message === undefined;
  };

  const commit = (): void => {
    const val = convertStringValToNum(editedValue);
    if (!validate(val)) {
      onChange(undefined);
      return;
    }
    onChange(val);
  };

  const handleChange = (val: string): void => {
    setEditedValue(val);
    validate(convertStringValToNum(val));
  };

  return (
    <TextField
      inputRef={inputRef}
      type="number"
      label={label}
      size="small"
      {...restTextFieldProps}
      slotProps={{
        ...slotProps,
        htmlInput: {
          ...slotProps?.htmlInput,
          ...(step !== undefined ? { step } : {}),
        },
      }}
      // work around an ancient bug in firefox with focus not getting fired
      // on unfocused number spinners when you use the arrow buttons:
      // https://bugzilla.mozilla.org/show_bug.cgi?id=1012818
      onClick={() => {
        inputRef.current?.focus();
      }}
      onChange={(e) => {
        handleChange(e.target.value);
      }}
      value={editedValue}
      onBlur={commit}
      onKeyDown={({ key }) => {
        if (key === "Enter") {
          commit();
        }
      }}
      error={Boolean(isNil(error) ? internalError : error)}
      helperText={isNil(error) ? internalError : error}
    />
  );
};
